import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Plus,
    Trash2,
    Edit3,
    X,
    Sparkles,
    Eye,
    EyeOff,
    Search,
    UserCheck,
    UserMinus,
    UserPlus,
    Loader2,
    Save,
    RefreshCw,
    Brain,
    Trophy,
    BarChart3,
    Target,
    Filter,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import FileUpload from "../../components/FileUpload";
import { useConfirm } from "../../components/ConfirmDialog";
import ThemedSelect from "../../components/ThemedSelect";
import PartnerAccountTab from "./PartnerAccountTab";
import AssoResourcesTab from "../../components/partner/admin/AssoResourcesTab";
import NewsEditor from "./NewsEditor";

const SECTION_FIELDS = [
    { id: "history", label: "Histoire / Origine" },
    { id: "mission", label: "Mission" },
    { id: "actions", label: "Actions" },
    { id: "victories", label: "Réussites / Victoires", structured: true },
    { id: "chiffres_cles", label: "Chiffres clés" },
    { id: "argumentaire", label: "Argumentaire terrain" },
];

/**
 * Iter-32 — La génération IA (contenu ~65s, quiz ~50s, les deux ~115s) dépasse
 * le timeout Cloudflare (~100s) en production → erreur 524. Les endpoints sont
 * désormais asynchrones : POST lance le job, puis on poll gen-status jusqu'à
 * ready/failed. Retourne la réponse complète du gen-status (incl. `association`
 * fraîche) quand le job est terminé.
 */
async function pollGenStatus(aid, kind, { intervalMs = 4000, timeoutMs = 300000 } = {}) {
    const t0 = Date.now();
    while (Date.now() - t0 < timeoutMs) {
        await new Promise((r) => setTimeout(r, intervalMs));
        let data;
        try {
            ({ data } = await api.get(`/admin/associations/${aid}/gen-status`));
        } catch {
            continue; // erreur réseau transitoire — on réessaie au prochain tick
        }
        const job = data?.[kind];
        if (job?.status === "ready") return data;
        if (job?.status === "failed") {
            const err = new Error(job.error || "La génération a échoué. Réessaie.");
            err.generation = true;
            throw err;
        }
    }
    const err = new Error("La génération prend trop de temps — réessaie dans un instant.");
    err.generation = true;
    throw err;
}

function VisibilityEditor({ asso, users, onSaved }) {
    const [mode, setMode] = useState(asso.visibility?.mode || "specific");
    const [selected, setSelected] = useState(new Set(asso.visibility?.user_ids || []));
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const learners = useMemo(
        () => users.filter((u) => u.role === "learner" || u.role === "manager"),
        [users],
    );
    const filtered = useMemo(() => {
        if (!search.trim()) return learners;
        const q = search.toLowerCase();
        return learners.filter(
            (u) =>
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q),
        );
    }, [learners, search]);

    const toggle = (uid) => {
        const next = new Set(selected);
        next.has(uid) ? next.delete(uid) : next.add(uid);
        setSelected(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/associations/${asso.association_id}/visibility`, {
                mode,
                user_ids: Array.from(selected),
            });
            toast.success("Visibilité mise à jour");
            onSaved?.();
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Iter-27.24 — explainer to clarify Visibilité vs Assignation terrain */}
            <div className="rounded-2xl bg-white/4 border border-white/10 p-3.5">
                <div className="flex items-start gap-3">
                    <Eye className="w-4 h-4 text-white/55 shrink-0 mt-0.5" />
                    <div className="text-xs text-white/65 leading-relaxed space-y-1">
                        <p>
                            <span className="font-bold text-white">
                                Visibilité = qui peut VOIR la fiche de l'asso
                            </span>{" "}
                            (lire la mission, le contenu, faire le quiz). Ne crée pas
                            d'affectation à la mission terrain.
                        </p>
                        <p className="text-white/45">
                            Pour positionner explicitement des recruteurs sur la mission terrain
                            (badge « TON ASSO », carte « Mon affectation »), utilise l'onglet{" "}
                            <span className="font-bold text-emerald-300">
                                Assignation terrain
                            </span>
                            .
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setMode("specific")}
                    data-testid="visibility-mode-specific"
                    className={`flex-1 px-4 py-3 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        mode === "specific"
                            ? "bg-shariti-orange text-white"
                            : "bg-white/5 text-white/60 hover:bg-white/10"
                    }`}
                >
                    <UserCheck className="w-4 h-4" />
                    Liste restreinte ({selected.size})
                </button>
                <button
                    type="button"
                    onClick={() => setMode("all")}
                    data-testid="visibility-mode-all"
                    className={`flex-1 px-4 py-3 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        mode === "all"
                            ? "bg-shariti-orange text-white"
                            : "bg-white/5 text-white/60 hover:bg-white/10"
                    }`}
                >
                    <Eye className="w-4 h-4" />
                    Tout le monde
                </button>
            </div>

            {mode === "all" && (
                <div className="rounded-2xl bg-white/4 border border-white/10 p-4 space-y-3" data-testid="visibility-all-summary">
                    <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-white/55" />
                        <p className="text-xs font-extrabold tracking-[0.15em] uppercase text-white/70">
                            Tout le monde peut VOIR cette fiche
                        </p>
                    </div>
                    {(() => {
                        const assignedIds = new Set(asso.assigned_user_ids || []);
                        const assignedUsers = learners.filter((u) => assignedIds.has(u.user_id));
                        if (assignedUsers.length === 0) {
                            return (
                                <p className="text-xs text-white/50 italic leading-relaxed">
                                    Aucun apprenant n&apos;est encore affecté à la mission terrain de cette association.
                                    Va dans l&apos;onglet <span className="font-bold text-emerald-300">Assignation terrain</span> pour positionner les recruteurs sur cette mission.
                                </p>
                            );
                        }
                        return (
                            <>
                                <p className="text-[11px] text-white/55 leading-relaxed">
                                    Voici les <span className="font-bold text-white">{assignedUsers.length}</span> apprenant{assignedUsers.length > 1 ? "s" : ""} explicitement affecté{assignedUsers.length > 1 ? "s" : ""} à la mission terrain (depuis « Assignation terrain ») :
                                </p>
                                <div className="max-h-40 overflow-y-auto pr-1 space-y-1">
                                    {assignedUsers.map((u) => (
                                        <div
                                            key={u.user_id}
                                            className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl bg-emerald-500/5 border border-emerald-500/15"
                                            data-testid={`visibility-all-assigned-${u.user_id}`}
                                        >
                                            {u.avatar ? (
                                                <img src={u.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                                            ) : (
                                                <span className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center text-[10px] font-extrabold text-emerald-300">
                                                    {(u.name || "?").slice(0, 1).toUpperCase()}
                                                </span>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-white truncate">{u.name}</p>
                                                <p className="text-[10px] text-white/45 truncate">{u.email}</p>
                                            </div>
                                            <span className="text-[9px] font-extrabold tracking-[0.15em] uppercase text-emerald-300 shrink-0">{u.role}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        );
                    })()}
                </div>
            )}

            {mode === "specific" && (
                <>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher un recruteur..."
                            data-testid="visibility-search"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-2.5 text-white placeholder-white/30 outline-none text-sm focus:border-shariti-orange/40"
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto pr-1 space-y-1">
                        {filtered.length === 0 && (
                            <p className="text-xs text-white/40 italic text-center py-4">
                                Aucun utilisateur correspondant.
                            </p>
                        )}
                        {filtered.map((u) => {
                            const isOn = selected.has(u.user_id);
                            return (
                                <button
                                    key={u.user_id}
                                    type="button"
                                    onClick={() => toggle(u.user_id)}
                                    data-testid={`visibility-user-${u.user_id}`}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                                        isOn
                                            ? "bg-shariti-orange/15 border border-shariti-orange/30"
                                            : "hover:bg-white/5 border border-transparent"
                                    }`}
                                >
                                    <span
                                        className={`w-4 h-4 rounded border-2 shrink-0 flex items-center justify-center ${
                                            isOn
                                                ? "bg-shariti-orange border-shariti-orange"
                                                : "border-white/25"
                                        }`}
                                    >
                                        {isOn && <span className="w-1.5 h-1.5 bg-white rounded-sm" />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-white truncate">
                                            {u.name}
                                        </p>
                                        <p className="text-[11px] text-white/45 truncate">
                                            {u.email} · {u.role}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="visibility-save"
                className="w-full bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Enregistrer la visibilité
            </button>
        </div>
    );
}

function QuizEditor({ associationId }) {
    const [questions, setQuestions] = useState(null); // null = loading, [] = no quiz yet
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);

    const reload = () => {
        api.get(`/admin/associations/${associationId}/quiz`).then((r) => {
            setQuestions(r.data.questions || []);
        });
    };
    useEffect(() => {
        reload();
    }, [associationId]);

    const generate = async () => {
        setGenerating(true);
        try {
            try {
                await api.post(`/admin/associations/${associationId}/quiz/generate`);
                toast.info("Génération du quiz lancée — jusqu'à 2 minutes…");
            } catch (e) {
                // 409 = un job tourne déjà → on rejoint le polling
                if (e.response?.status !== 409) throw e;
                toast.info("Une génération est déjà en cours — on attend le résultat…");
            }
            await pollGenStatus(associationId, "quiz");
            reload();
            toast.success("Quiz généré par l'IA");
        } catch (e) {
            toast.error(
                e.generation
                    ? e.message
                    : e.response?.data?.detail || "Échec génération",
            );
        } finally {
            setGenerating(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/associations/${associationId}/quiz`, { questions });
            toast.success("Quiz enregistré");
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    if (questions === null) return <p className="text-white/40 italic text-sm">Chargement...</p>;

    return (
        <div className="space-y-4">
            <button
                type="button"
                onClick={generate}
                disabled={generating}
                data-testid="quiz-generate"
                className="w-full glass-card rounded-2xl p-3 border border-shariti-orange/30 hover:border-shariti-orange/60 flex items-center justify-center gap-2 text-sm font-extrabold text-white disabled:opacity-60"
            >
                {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin text-shariti-orange" />
                ) : (
                    <Sparkles className="w-4 h-4 text-shariti-orange" />
                )}
                {questions.length > 0 ? "Régénérer le quiz avec l'IA" : "Générer un quiz avec l'IA (5 questions)"}
            </button>

            {questions.length === 0 && (
                <p className="text-xs text-white/45 italic text-center py-3">
                    Aucun quiz pour le moment. Lance la génération IA ou ajoute des questions manuellement.
                </p>
            )}

            {questions.map((q, qi) => (
                <div
                    key={qi}
                    className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3"
                    data-testid={`quiz-q-${qi}`}
                >
                    <div className="flex items-start justify-between gap-2">
                        <input
                            type="text"
                            value={q.question}
                            onChange={(e) => {
                                const next = [...questions];
                                next[qi] = { ...q, question: e.target.value };
                                setQuestions(next);
                            }}
                            placeholder="Question"
                            className="flex-1 bg-transparent text-sm font-bold text-white outline-none placeholder-white/30"
                        />
                        <button
                            type="button"
                            onClick={() => setQuestions(questions.filter((_, i) => i !== qi))}
                            className="text-[10px] font-bold text-red-400 hover:text-red-300 px-2"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="space-y-1.5">
                        {q.options.map((opt, oi) => (
                            <div
                                key={oi}
                                className={`flex items-center gap-2 px-3 py-2 rounded-xl ${
                                    q.correct_index === oi
                                        ? "bg-shariti-orange/15 border border-shariti-orange/40"
                                        : "bg-white/[0.03]"
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        const next = [...questions];
                                        next[qi] = { ...q, correct_index: oi };
                                        setQuestions(next);
                                    }}
                                    title="Marquer comme bonne réponse"
                                    className={`w-4 h-4 rounded-full border-2 shrink-0 ${
                                        q.correct_index === oi
                                            ? "border-shariti-orange bg-shariti-orange"
                                            : "border-white/30"
                                    }`}
                                />
                                <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) => {
                                        const next = [...questions];
                                        const opts = [...q.options];
                                        opts[oi] = e.target.value;
                                        next[qi] = { ...q, options: opts };
                                        setQuestions(next);
                                    }}
                                    placeholder={`Option ${oi + 1}`}
                                    className="flex-1 bg-transparent text-xs text-white/85 outline-none placeholder-white/30"
                                />
                            </div>
                        ))}
                    </div>
                    <textarea
                        rows={2}
                        value={q.explanation || ""}
                        onChange={(e) => {
                            const next = [...questions];
                            next[qi] = { ...q, explanation: e.target.value };
                            setQuestions(next);
                        }}
                        placeholder="Explication (visible après réponse)"
                        className="w-full bg-white/[0.03] rounded-xl px-3 py-2 text-xs text-white/70 outline-none resize-y border border-white/5 placeholder-white/30"
                    />
                </div>
            ))}

            {questions.length > 0 && (
                <button
                    type="button"
                    onClick={() =>
                        setQuestions([
                            ...questions,
                            {
                                question: "",
                                options: ["", "", "", ""],
                                correct_index: 0,
                                explanation: "",
                            },
                        ])
                    }
                    className="w-full text-xs font-bold text-shariti-orange-light hover:text-shariti-orange py-2 rounded-xl border border-dashed border-white/15 hover:border-shariti-orange/40"
                >
                    + Ajouter une question
                </button>
            )}

            {questions.length > 0 && (
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="quiz-save"
                    className="w-full bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light flex items-center justify-center gap-2 disabled:opacity-60"
                >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <Save className="w-4 h-4" /> Enregistrer le quiz
                </button>
            )}
        </div>
    );
}

function StatsView({ associationId }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/admin/associations/${associationId}/stats`).then((r) => setData(r.data));
    }, [associationId]);
    if (!data) return <p className="text-white/40 italic text-sm">Chargement...</p>;
    if (!data.rows.length) {
        return (
            <div className="text-center py-8">
                <BarChart3 className="w-10 h-10 text-shariti-orange-light mx-auto mb-3" />
                <p className="text-sm text-white/65 font-light max-w-xs mx-auto">
                    Aucune activité pour le moment. Les stats apparaîtront dès que les recruteurs commenceront à explorer cette association.
                </p>
            </div>
        );
    }
    const passed = data.rows.filter((r) => r.last_quiz_passed).length;
    const tested = data.rows.filter((r) => r.quiz_attempts_count > 0).length;
    return (
        <div className="space-y-4" data-testid="asso-stats">
            <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/5 rounded-2xl p-3">
                    <p className="text-[10px] uppercase tracking-widest text-white/45 font-thin">Recruteurs</p>
                    <p className="text-2xl font-extrabold text-white">{data.rows.length}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-3">
                    <p className="text-[10px] uppercase tracking-widest text-white/45 font-thin">Quiz passés</p>
                    <p className="text-2xl font-extrabold text-white">
                        {tested}
                        <span className="text-xs text-white/50 font-bold">/{data.rows.length}</span>
                    </p>
                </div>
                <div className="bg-white/5 rounded-2xl p-3">
                    <p className="text-[10px] uppercase tracking-widest text-white/45 font-thin">Validés ≥70%</p>
                    <p className="text-2xl font-extrabold text-shariti-orange">{passed}</p>
                </div>
            </div>

            <div className="space-y-1">
                {data.rows.map((r) => (
                    <div
                        key={r.user_id}
                        className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5"
                        data-testid={`stats-row-${r.user_id}`}
                    >
                        {r.avatar ? (
                            <img src={r.avatar} alt="" className="w-9 h-9 rounded-xl object-cover shrink-0" />
                        ) : (
                            <div className="w-9 h-9 rounded-xl bg-shariti-orange/20 flex items-center justify-center font-extrabold text-white shrink-0">
                                {(r.name || "?")[0]?.toUpperCase()}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white truncate">{r.name}</p>
                            <p className="text-[11px] text-white/45 truncate">{r.email}</p>
                        </div>
                        <div className="flex items-center gap-3 text-[11px]">
                            <div className="text-center">
                                <p className="text-white/35 font-thin uppercase tracking-widest">Sections</p>
                                <p className="font-extrabold text-white">{r.sections_read_count}/5</p>
                            </div>
                            <div className="text-center">
                                <p className="text-white/35 font-thin uppercase tracking-widest">Tentatives</p>
                                <p className="font-extrabold text-white">{r.quiz_attempts_count}</p>
                            </div>
                            <div className="text-center w-14">
                                <p className="text-white/35 font-thin uppercase tracking-widest">Best</p>
                                {r.best_quiz_score != null ? (
                                    <p
                                        className={`font-extrabold ${
                                            r.best_quiz_score >= 70 ? "text-shariti-orange" : "text-shariti-yellow"
                                        }`}
                                    >
                                        {r.best_quiz_score}%
                                    </p>
                                ) : (
                                    <p className="text-white/30">—</p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AssignmentsEditor({ asso, onSaved }) {
    const [data, setData] = useState(null); // { assigned_users, assigned_count }
    const [allUsers, setAllUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("learner"); // learner|manager|admin|all
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [view, setView] = useState("pick"); // pick = catalog, current = assignés

    // Initial load
    useEffect(() => {
        api.get(`/admin/associations/${asso.association_id}/assignments`).then((r) =>
            setData(r.data),
        );
        api.get("/admin/users", { params: { page_size: 5000 } }).then((r) =>
            setAllUsers(r.data.items || []),
        );
    }, [asso.association_id]);

    const assignedIdSet = useMemo(
        () => new Set((data?.assigned_users || []).map((u) => u.user_id)),
        [data],
    );

    // Filtered + searched user list (for the picker)
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allUsers.filter((u) => {
            if (roleFilter !== "all" && u.role !== roleFilter) return false;
            if (!q) return true;
            return (
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q)
            );
        });
    }, [allUsers, search, roleFilter]);

    const visibleUnassigned = filtered.filter((u) => !assignedIdSet.has(u.user_id));
    const visibleSelectedCount = visibleUnassigned.filter((u) => selectedIds.has(u.user_id)).length;
    const allVisibleSelected =
        visibleUnassigned.length > 0 && visibleSelectedCount === visibleUnassigned.length;

    const toggle = (uid) => {
        const next = new Set(selectedIds);
        if (next.has(uid)) next.delete(uid);
        else next.add(uid);
        setSelectedIds(next);
    };

    const selectAllVisible = () => {
        const next = new Set(selectedIds);
        if (allVisibleSelected) {
            visibleUnassigned.forEach((u) => next.delete(u.user_id));
        } else {
            visibleUnassigned.forEach((u) => next.add(u.user_id));
        }
        setSelectedIds(next);
    };

    const refreshAssigned = async () => {
        const r = await api.get(`/admin/associations/${asso.association_id}/assignments`);
        setData(r.data);
        setSelectedIds(new Set());
        if (onSaved) onSaved();
    };

    const bulkAdd = async () => {
        if (selectedIds.size === 0) return;
        setSaving(true);
        try {
            await api.patch(`/admin/associations/${asso.association_id}/assignments`, {
                add: Array.from(selectedIds),
            });
            toast.success(`${selectedIds.size} recruteur·s assigné·s à la mission`);
            await refreshAssigned();
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "Erreur lors de l'assignation",
            );
        } finally {
            setSaving(false);
        }
    };

    const removeOne = async (uid) => {
        setSaving(true);
        try {
            await api.patch(`/admin/associations/${asso.association_id}/assignments`, {
                remove: [uid],
            });
            toast.success("Recruteur retiré de la mission");
            await refreshAssigned();
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) || "Erreur lors du retrait",
            );
        } finally {
            setSaving(false);
        }
    };

    const bulkAddAllMatching = async () => {
        // Use backend query-based bulk for true scalability
        setSaving(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("q", search.trim());
            if (roleFilter !== "all") params.set("role", roleFilter);
            params.set("mode", "add");
            const r = await api.post(
                `/admin/associations/${asso.association_id}/assignments/from-query?${params}`,
            );
            toast.success(
                `${r.data.diff_count > 0 ? "+" : ""}${r.data.diff_count} recruteur·s assigné·s ` +
                    `(total: ${r.data.assigned_count})`,
            );
            await refreshAssigned();
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "Erreur lors de l'assignation par filtre",
            );
        } finally {
            setSaving(false);
        }
    };

    const clearAll = async () => {
        if (!data || data.assigned_count === 0) return;
        setSaving(true);
        try {
            await api.patch(`/admin/associations/${asso.association_id}/assignments`, {
                set_all: [],
            });
            toast.success("Tous les assignés ont été retirés");
            await refreshAssigned();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    if (!data) {
        return (
            <div className="glass-card rounded-2xl p-8 text-center text-white/55">
                Chargement…
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="assignments-editor">
            {/* Explainer header */}
            <div className="rounded-2xl bg-emerald-500/8 border border-emerald-500/25 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
                    <div className="space-y-1.5 text-sm text-white/80 leading-relaxed">
                        <p>
                            <span className="font-extrabold text-emerald-300">
                                Assignation terrain
                            </span>{" "}
                            = qui est positionné·e sur la mission (badge{" "}
                            <span className="text-emerald-300 font-bold">TON ASSO</span> dans le
                            profil + leçon « Le montant »). Distinct de l'onglet{" "}
                            <span className="font-bold text-white">Visibilité</span> qui contrôle{" "}
                            <em>qui peut voir la fiche</em> de l'asso.
                        </p>
                        <p className="text-xs text-white/55">
                            Un recruteur peut voir une asso sans y être assigné (auto-formation),
                            ou être assigné à plusieurs missions en même temps.
                        </p>
                    </div>
                </div>
            </div>

            {/* Big counter + actions header */}
            <div className="glass-card rounded-2xl p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-emerald-300 mb-0.5">
                        Recruteurs assignés
                    </p>
                    <p
                        className="text-3xl font-extrabold text-white"
                        data-testid="assignments-count"
                    >
                        {data.assigned_count}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setView(view === "pick" ? "current" : "pick")}
                        className="text-xs font-bold px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-center gap-1.5"
                        data-testid="assignments-toggle-view"
                    >
                        {view === "pick" ? (
                            <>
                                <Eye className="w-3.5 h-3.5" />
                                Voir les {data.assigned_count} assignés
                            </>
                        ) : (
                            <>
                                <UserPlus className="w-3.5 h-3.5" />
                                Ajouter des recruteurs
                            </>
                        )}
                    </button>
                    {data.assigned_count > 0 && (
                        <button
                            onClick={clearAll}
                            disabled={saving}
                            className="text-xs font-bold px-3 py-2 rounded-xl bg-red-500/8 hover:bg-red-500/15 border border-red-500/20 text-red-300 transition-all flex items-center gap-1.5"
                            data-testid="assignments-clear-all"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            Tout retirer
                        </button>
                    )}
                </div>
            </div>

            {view === "current" ? (
                /* Current assignees list */
                <div className="space-y-2">
                    {data.assigned_users.length === 0 ? (
                        <div className="glass-card rounded-2xl p-8 text-center">
                            <p className="text-sm text-white/55">
                                Aucun recruteur n'est encore assigné à cette mission terrain.
                            </p>
                            <button
                                onClick={() => setView("pick")}
                                className="mt-3 text-xs font-bold text-shariti-orange-light hover:text-shariti-orange"
                            >
                                Ajouter des recruteurs →
                            </button>
                        </div>
                    ) : (
                        data.assigned_users.map((u) => (
                            <div
                                key={u.user_id}
                                className="glass-card rounded-xl p-3 flex items-center gap-3"
                                data-testid={`assigned-${u.user_id}`}
                            >
                                <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                                    {u.avatar ? (
                                        <img
                                            src={u.avatar}
                                            className="w-10 h-10 rounded-full object-cover"
                                            alt=""
                                        />
                                    ) : (
                                        <span className="text-sm font-bold text-emerald-300">
                                            {(u.name || "?").slice(0, 1).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-white truncate">
                                        {u.name}
                                    </p>
                                    <p className="text-xs text-white/45 truncate">{u.email}</p>
                                </div>
                                <span className="text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full bg-white/8 text-white/60">
                                    {u.role}
                                </span>
                                <button
                                    onClick={() => removeOne(u.user_id)}
                                    disabled={saving}
                                    data-testid={`assigned-remove-${u.user_id}`}
                                    className="p-2 rounded-lg hover:bg-red-500/10 text-red-300 transition-colors"
                                    title="Retirer"
                                >
                                    <UserMinus className="w-4 h-4" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            ) : (
                /* Picker */
                <>
                    <div className="glass-card rounded-2xl p-3 sm:p-4 space-y-3">
                        <div className="flex gap-2 flex-wrap items-center">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Rechercher par nom ou email…"
                                    data-testid="assignments-search"
                                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-shariti-orange/40"
                                />
                            </div>
                            {/* Iter-27.61 — ThemedSelect Shariti */}
                            <div className="min-w-[160px]">
                                <ThemedSelect
                                    value={roleFilter}
                                    onChange={setRoleFilter}
                                    dataTestid="assignments-role-filter"
                                    size="sm"
                                    options={[
                                        { value: "learner", label: "Recruteurs" },
                                        { value: "manager", label: "Managers" },
                                        { value: "admin", label: "Admins" },
                                        { value: "all", label: "Tous les rôles" },
                                    ]}
                                />
                            </div>
                        </div>
                        {(search || roleFilter !== "learner") && (
                            <button
                                onClick={bulkAddAllMatching}
                                disabled={saving || filtered.length === 0}
                                data-testid="assignments-bulk-from-query"
                                className="w-full bg-shariti-orange/15 hover:bg-shariti-orange/25 border border-shariti-orange/30 rounded-xl py-2.5 text-xs font-bold text-shariti-orange-light transition-all flex items-center justify-center gap-2"
                            >
                                <Filter className="w-3.5 h-3.5" />
                                Assigner les {filtered.length} résultats correspondant au filtre
                            </button>
                        )}
                    </div>

                    {selectedIds.size > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-2xl bg-shariti-orange/12 border border-shariti-orange/30 p-3 flex items-center justify-between gap-2 flex-wrap sticky top-2 z-10 backdrop-blur"
                            data-testid="assignments-selection-bar"
                        >
                            <p className="text-sm font-bold text-shariti-orange-light">
                                {selectedIds.size} sélectionné·s
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setSelectedIds(new Set())}
                                    className="text-xs font-bold text-white/60 hover:text-white px-3 py-1.5"
                                >
                                    Effacer
                                </button>
                                <button
                                    onClick={bulkAdd}
                                    disabled={saving}
                                    data-testid="assignments-bulk-add"
                                    className="bg-shariti-orange text-white text-xs font-extrabold px-4 py-1.5 rounded-lg orange-glow flex items-center gap-1.5"
                                >
                                    {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                                    <UserPlus className="w-3.5 h-3.5" />
                                    Assigner les {selectedIds.size}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
                        {visibleUnassigned.length > 0 && (
                            <button
                                onClick={selectAllVisible}
                                data-testid="assignments-select-all-visible"
                                className="w-full text-left text-xs font-bold text-white/60 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-2"
                            >
                                <span
                                    className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                                        allVisibleSelected
                                            ? "bg-shariti-orange border-shariti-orange"
                                            : "border-white/30"
                                    }`}
                                >
                                    {allVisibleSelected && (
                                        <CheckCircle2 className="w-3 h-3 text-white" />
                                    )}
                                </span>
                                {allVisibleSelected
                                    ? `Désélectionner les ${visibleUnassigned.length} visibles`
                                    : `Sélectionner les ${visibleUnassigned.length} non-assignés visibles`}
                            </button>
                        )}
                        {visibleUnassigned.length === 0 ? (
                            <div className="glass-card rounded-xl p-5 text-center text-xs text-white/55">
                                {filtered.length === 0
                                    ? "Aucun utilisateur ne correspond à ces filtres."
                                    : "Tous les utilisateurs filtrés sont déjà assignés à cette mission."}
                            </div>
                        ) : (
                            visibleUnassigned.map((u) => {
                                const isSelected = selectedIds.has(u.user_id);
                                return (
                                    <button
                                        key={u.user_id}
                                        onClick={() => toggle(u.user_id)}
                                        data-testid={`assignments-pick-${u.user_id}`}
                                        className={`w-full glass-card rounded-xl p-2.5 flex items-center gap-3 transition-all border ${
                                            isSelected
                                                ? "border-shariti-orange/50 bg-shariti-orange/8"
                                                : "border-transparent hover:border-white/15"
                                        }`}
                                    >
                                        <span
                                            className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                                                isSelected
                                                    ? "bg-shariti-orange border-shariti-orange"
                                                    : "border-white/30"
                                            }`}
                                        >
                                            {isSelected && (
                                                <CheckCircle2 className="w-3 h-3 text-white" />
                                            )}
                                        </span>
                                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                                            {u.avatar ? (
                                                <img
                                                    src={u.avatar}
                                                    className="w-8 h-8 rounded-full object-cover"
                                                    alt=""
                                                />
                                            ) : (
                                                <span className="text-xs font-bold text-white/60">
                                                    {(u.name || "?").slice(0, 1).toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                            <p className="text-sm font-bold text-white truncate">
                                                {u.name}
                                            </p>
                                            <p className="text-xs text-white/45 truncate">
                                                {u.email}
                                            </p>
                                        </div>
                                        <span className="text-[9px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full bg-white/8 text-white/55">
                                            {u.role}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </>
            )}
        </div>
    );
}


function GiftLevelsEditor({ form, setForm }) {
    const levels = form.gift_levels || [];
    const addLevel = () => {
        setForm({
            ...form,
            gift_levels: [
                ...levels,
                { amount: 15, label: "", impact: "" },
            ],
        });
    };
    const updateLevel = (idx, field, value) => {
        const next = [...levels];
        next[idx] = {
            ...next[idx],
            [field]: field === "amount" ? Number(value) || 0 : value,
        };
        setForm({ ...form, gift_levels: next });
    };
    const removeLevel = (idx) => {
        setForm({ ...form, gift_levels: levels.filter((_, i) => i !== idx) });
    };

    return (
        <div className="space-y-4" data-testid="gift-levels-editor">
            <div className="rounded-2xl bg-shariti-orange/8 border border-shariti-orange/20 p-4 sm:p-5">
                <p className="text-sm text-white/75 leading-relaxed">
                    Ces paliers s'affichent dans la leçon{" "}
                    <span className="font-bold text-shariti-orange-light">
                        « Le montant »
                    </span>{" "}
                    du module{" "}
                    <span className="font-bold text-shariti-orange-light">
                        L'art du don régulier
                    </span>
                    . Chaque palier transforme un montant en{" "}
                    <span className="font-bold text-white">action concrète</span> que le recruteur
                    pourra raconter sur le terrain.
                </p>
            </div>

            {levels.length === 0 && (
                <div className="glass-card rounded-2xl p-6 text-center">
                    <p className="text-white/55 text-sm mb-3">
                        Aucun palier configuré pour cette association.
                    </p>
                    <button
                        onClick={addLevel}
                        data-testid="gift-levels-add-first"
                        className="bg-shariti-orange text-white font-bold py-2.5 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                    >
                        + Ajouter un premier palier
                    </button>
                </div>
            )}

            {levels.map((g, i) => (
                <div
                    key={i}
                    className="glass-card rounded-2xl p-4 sm:p-5 space-y-3"
                    data-testid={`gift-level-${i}`}
                >
                    <div className="flex items-center justify-between">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            Palier {i + 1}
                        </p>
                        <button
                            type="button"
                            onClick={() => removeLevel(i)}
                            data-testid={`gift-level-remove-${i}`}
                            className="text-xs text-red-300 hover:text-red-200 font-bold"
                        >
                            Supprimer
                        </button>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Montant (€/mois)
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={g.amount}
                                onChange={(e) => updateLevel(i, "amount", e.target.value)}
                                data-testid={`gift-level-amount-${i}`}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none focus:border-shariti-orange/40"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Libellé court (optionnel)
                            </label>
                            <input
                                type="text"
                                value={g.label || ""}
                                placeholder="ex: Minimum terrain, Engagement standard..."
                                onChange={(e) => updateLevel(i, "label", e.target.value)}
                                data-testid={`gift-level-label-${i}`}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none focus:border-shariti-orange/40"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Impact concret · racontable sur le terrain
                        </label>
                        <textarea
                            value={g.impact}
                            placeholder="ex: finance la production de rapports pour alerter les médias et l'opinion publique."
                            onChange={(e) => updateLevel(i, "impact", e.target.value)}
                            data-testid={`gift-level-impact-${i}`}
                            rows={2}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none focus:border-shariti-orange/40 text-sm"
                        />
                    </div>
                </div>
            ))}

            {levels.length > 0 && (
                <button
                    type="button"
                    onClick={addLevel}
                    data-testid="gift-levels-add"
                    className="w-full bg-white/5 hover:bg-white/8 border border-white/15 border-dashed rounded-2xl py-3 text-sm text-white/65 hover:text-white transition-all"
                >
                    + Ajouter un palier
                </button>
            )}
        </div>
    );
}

function VictoriesEditor({ items, onChange }) {
    const add = () =>
        onChange([
            ...(items || []),
            { title: "", year: "", description: "", impact: "" },
        ]);
    const update = (idx, field, value) => {
        const next = [...items];
        next[idx] = { ...next[idx], [field]: value };
        onChange(next);
    };
    const remove = (idx) => {
        const next = items.filter((_, i) => i !== idx);
        onChange(next);
    };
    return (
        <div className="space-y-2" data-testid="victories-editor">
            {(items || []).length === 0 && (
                <p className="text-[11px] text-white/40 italic px-1">
                    Aucune réussite. Clique sur "Régénérer" pour proposer 5-8 victoires concrètes via l'IA, ou ajoute-en manuellement.
                </p>
            )}
            {(items || []).map((v, idx) => (
                <div
                    key={idx}
                    className="rounded-xl border border-white/10 bg-white/4 p-2.5 space-y-2"
                    data-testid={`victory-editor-row-${idx}`}
                >
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={v.title || ""}
                            onChange={(e) => update(idx, "title", e.target.value)}
                            placeholder="Titre de la réussite"
                            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs outline-none focus:border-shariti-orange/40"
                            data-testid={`victory-title-${idx}`}
                        />
                        <input
                            type="text"
                            value={v.year || ""}
                            onChange={(e) => update(idx, "year", e.target.value)}
                            placeholder="Année"
                            className="w-20 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs outline-none focus:border-shariti-orange/40"
                            data-testid={`victory-year-${idx}`}
                        />
                        <button
                            type="button"
                            onClick={() => remove(idx)}
                            className="text-red-400 hover:text-red-300 text-xs px-2"
                            data-testid={`victory-remove-${idx}`}
                            aria-label="Supprimer cette réussite"
                        >
                            ×
                        </button>
                    </div>
                    <textarea
                        rows={2}
                        value={v.description || ""}
                        onChange={(e) => update(idx, "description", e.target.value)}
                        placeholder="Description concrète (2-3 phrases)"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs outline-none focus:border-shariti-orange/40 resize-y"
                        data-testid={`victory-desc-${idx}`}
                    />
                    <input
                        type="text"
                        value={v.impact || ""}
                        onChange={(e) => update(idx, "impact", e.target.value)}
                        placeholder="Impact chiffré (optionnel — ex: 20 000 vies épargnées)"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-shariti-orange-light text-xs outline-none focus:border-shariti-orange/40"
                        data-testid={`victory-impact-${idx}`}
                    />
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                className="w-full text-[11px] font-bold text-shariti-orange-light hover:text-shariti-orange bg-white/3 hover:bg-white/5 border border-dashed border-white/15 rounded-xl py-2 transition-all"
                data-testid="victory-add"
            >
                + Ajouter une réussite
            </button>
        </div>
    );
}


function AssociationEditor({ asso, users, onClose, onChanged, initialTab = "info" }) {
    const [form, setForm] = useState({
        name: asso.name || "",
        short_description: asso.short_description || "",
        logo_url: asso.logo_url || "",
        video_url: asso.video_url || "",
        history: asso.history || "",
        mission: asso.mission || "",
        actions: asso.actions || "",
        victories: asso.victories || [],
        chiffres_cles: asso.chiffres_cles || "",
        argumentaire: asso.argumentaire || "",
        faq: asso.faq || [],
        gift_levels: asso.gift_levels || [],
        is_published: asso.is_published !== false,
        // Iter-27.62 — Espace Premium Association
        tier: asso.tier || "engaged",
        primary_color: asso.primary_color || "",
        accent_color: asso.accent_color || "",
        hero_image_url: asso.hero_image_url || "",
        hero_video_url: asso.hero_video_url || "",
        tagline: asso.tagline || "",
        features: asso.features || {},
        // Iter-30 — Date d'entrée en partenariat (éditable)
        partner_since: asso.partner_since || "",
    });
    const [tab, setTab] = useState(initialTab); // info | content | premium | visibility
    const [videoMode, setVideoMode] = useState("url"); // url | upload
    const [saving, setSaving] = useState(false);
    const [regenLoading, setRegenLoading] = useState("");

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/associations/${asso.association_id}`, form);
            toast.success("Association mise à jour");
            onChanged?.();
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    const regenerate = async (section) => {
        setRegenLoading(section);
        try {
            try {
                await api.post(
                    `/admin/associations/${asso.association_id}/regenerate`,
                    { section },
                );
                toast.info("Génération lancée — jusqu'à 2 minutes, tu peux rester sur la page…");
            } catch (e) {
                // 409 = un job tourne déjà → on rejoint simplement le polling
                if (e.response?.status !== 409) throw e;
                toast.info("Une génération est déjà en cours — on attend le résultat…");
            }
            const data = await pollGenStatus(asso.association_id, "content");
            const fresh = data.association || {};
            if (section === "all") {
                setForm((f) => ({
                    ...f,
                    short_description: fresh.short_description ?? f.short_description,
                    history: fresh.history ?? f.history,
                    mission: fresh.mission,
                    actions: fresh.actions,
                    victories: fresh.victories ?? f.victories,
                    chiffres_cles: fresh.chiffres_cles,
                    argumentaire: fresh.argumentaire,
                    faq: fresh.faq,
                }));
            } else {
                setForm((f) => ({ ...f, [section]: fresh[section] }));
            }
            toast.success("Contenu régénéré");
            onChanged?.();
        } catch (e) {
            toast.error(
                e.generation
                    ? e.message
                    : e.response?.data?.detail || "Erreur de génération",
            );
        } finally {
            setRegenLoading("");
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="glass-card rounded-3xl p-6 pb-28 lg:pb-6 max-w-3xl w-full my-4 relative"
                data-testid="association-editor"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                    <X className="w-4 h-4 text-white/70" />
                </button>
                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                    Édition association
                </p>
                <h3 className="text-2xl font-extrabold text-white mb-4 tracking-tight pr-10">
                    {asso.name}
                </h3>

                <div className="flex gap-1 p-1 glass-card rounded-full w-fit mb-5 text-xs flex-wrap">
                    {[
                        { id: "info", label: "Infos" },
                        { id: "content", label: "Contenu IA" },
                        { id: "news", label: "Actualités" },
                        { id: "resources", label: "Ressources" },
                        { id: "quiz", label: "Quiz" },
                        { id: "gift_levels", label: "Paliers de don" },
                        { id: "assignments", label: "Assignation terrain" },
                        { id: "visibility", label: "Visibilité" },
                        { id: "partner", label: "Compte partenaire" },
                        { id: "stats", label: "Stats" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`asso-tab-${t.id}`}
                            className={`px-4 py-2 rounded-full font-bold transition-all ${
                                tab === t.id
                                    ? "bg-shariti-orange text-white"
                                    : "text-white/60 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "info" && (
                    <div className="space-y-5">
                        <div>
                            <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Nom
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="asso-name"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/40"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block">
                                    Description courte
                                </label>
                                <button
                                    type="button"
                                    onClick={() => regenerate("short_description")}
                                    disabled={regenLoading === "short_description"}
                                    data-testid="asso-shortdesc-ai"
                                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-shariti-orange-light hover:text-shariti-orange transition-colors disabled:opacity-50"
                                    title="Génère ou régénère la description courte avec Claude"
                                >
                                    <Sparkles className="w-3 h-3" />
                                    {regenLoading === "short_description"
                                        ? "Génération…"
                                        : form.short_description
                                          ? "Régénérer avec l'IA"
                                          : "Générer avec l'IA"}
                                </button>
                            </div>
                            <textarea
                                rows={2}
                                value={form.short_description}
                                onChange={(e) =>
                                    setForm({ ...form, short_description: e.target.value })
                                }
                                placeholder="Une phrase concise qui résume la cause (ou clique sur 'Générer avec l'IA')"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/40 placeholder:text-white/25"
                            />
                        </div>

                        {/* Logo upload (with preview) */}
                        <FileUpload
                            kind="image"
                            currentUrl={form.logo_url}
                            onUploaded={(url) => setForm({ ...form, logo_url: url })}
                            testId="asso-logo-upload"
                            label="Logo (PNG/JPG, max 10 Mo)"
                        />

                        {/* Iter-30 — Date d'entrée en partenariat (visible sur sidebar de l'asso) */}
                        <div>
                            <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Partenaire depuis
                            </label>
                            <input
                                type="month"
                                value={(form.partner_since || "").slice(0, 7)}
                                onChange={(e) =>
                                    setForm({ ...form, partner_since: e.target.value })
                                }
                                data-testid="asso-partner-since"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/40"
                            />
                            <p className="text-[10px] text-white/40 mt-1.5 leading-relaxed">
                                Affiché sur la sidebar de l&apos;espace partenaire : « Partenaire depuis {form.partner_since ? new Date(`${form.partner_since.slice(0,7)}-01`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "— pas encore défini, fallback création"} ». Si vide, on retombe sur la date de création du compte.
                            </p>
                        </div>

                        {/* Video — URL OR upload */}
                        <div className="space-y-2">
                            <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block">
                                Vidéo de présentation
                            </label>
                            <div className="flex gap-1 p-1 glass-card rounded-full w-fit text-xs">
                                <button
                                    type="button"
                                    onClick={() => setVideoMode("url")}
                                    data-testid="video-mode-url"
                                    className={`px-4 py-1.5 rounded-full font-bold transition-all ${
                                        videoMode === "url"
                                            ? "bg-shariti-orange text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                >
                                    Coller une URL
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setVideoMode("upload")}
                                    data-testid="video-mode-upload"
                                    className={`px-4 py-1.5 rounded-full font-bold transition-all ${
                                        videoMode === "upload"
                                            ? "bg-shariti-orange text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                >
                                    Uploader un fichier
                                </button>
                            </div>
                            {videoMode === "url" ? (
                                <input
                                    type="text"
                                    placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                                    value={form.video_url}
                                    onChange={(e) =>
                                        setForm({ ...form, video_url: e.target.value })
                                    }
                                    data-testid="asso-video-url"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/40 text-sm"
                                />
                            ) : (
                                <FileUpload
                                    kind="video"
                                    currentUrl={form.video_url}
                                    onUploaded={(url) =>
                                        setForm({ ...form, video_url: url })
                                    }
                                    testId="asso-video-upload"
                                />
                            )}
                            {form.video_url && (
                                <p className="text-[11px] text-white/40 truncate">
                                    Actuel : {form.video_url}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {tab === "content" && (
                    <div className="space-y-4">
                        <button
                            type="button"
                            onClick={() => regenerate("all")}
                            disabled={!!regenLoading}
                            data-testid="regen-all"
                            className="w-full glass-card rounded-2xl p-3 border border-shariti-orange/30 hover:border-shariti-orange/60 transition-all flex items-center justify-center gap-2 text-sm font-extrabold text-white disabled:opacity-60"
                        >
                            {regenLoading === "all" ? (
                                <Loader2 className="w-4 h-4 animate-spin text-shariti-orange" />
                            ) : (
                                <Sparkles className="w-4 h-4 text-shariti-orange" />
                            )}
                            Tout régénérer avec l'IA
                        </button>
                        {SECTION_FIELDS.map((s) => (
                            <div key={s.id}>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50">
                                        {s.label} {s.structured && `(${(form.victories || []).length})`}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => regenerate(s.id)}
                                        disabled={!!regenLoading}
                                        data-testid={`regen-${s.id}`}
                                        className="text-[10px] font-bold text-shariti-orange-light hover:text-shariti-orange flex items-center gap-1 disabled:opacity-50"
                                    >
                                        {regenLoading === s.id ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <RefreshCw className="w-3 h-3" />
                                        )}
                                        Régénérer
                                    </button>
                                </div>
                                {s.structured ? (
                                    <VictoriesEditor
                                        items={form.victories || []}
                                        onChange={(items) => setForm({ ...form, victories: items })}
                                    />
                                ) : (
                                    <textarea
                                        rows={4}
                                        value={form[s.id]}
                                        onChange={(e) => setForm({ ...form, [s.id]: e.target.value })}
                                        data-testid={`asso-field-${s.id}`}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/40 resize-y"
                                    />
                                )}
                            </div>
                        ))}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50">
                                    FAQ ({form.faq.length})
                                </label>
                                <button
                                    type="button"
                                    onClick={() => regenerate("faq")}
                                    disabled={!!regenLoading}
                                    data-testid="regen-faq"
                                    className="text-[10px] font-bold text-shariti-orange-light hover:text-shariti-orange flex items-center gap-1 disabled:opacity-50"
                                >
                                    {regenLoading === "faq" ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <RefreshCw className="w-3 h-3" />
                                    )}
                                    Régénérer
                                </button>
                            </div>
                            <div className="space-y-2">
                                {form.faq.map((f, i) => (
                                    <div
                                        key={i}
                                        className="bg-white/5 border border-white/10 rounded-2xl p-3 space-y-2"
                                    >
                                        <input
                                            type="text"
                                            value={f.question}
                                            onChange={(e) => {
                                                const next = [...form.faq];
                                                next[i] = { ...next[i], question: e.target.value };
                                                setForm({ ...form, faq: next });
                                            }}
                                            placeholder="Question"
                                            className="w-full bg-transparent text-white text-sm font-bold outline-none placeholder-white/40"
                                        />
                                        <textarea
                                            rows={2}
                                            value={f.answer}
                                            onChange={(e) => {
                                                const next = [...form.faq];
                                                next[i] = { ...next[i], answer: e.target.value };
                                                setForm({ ...form, faq: next });
                                            }}
                                            placeholder="Réponse"
                                            className="w-full bg-transparent text-white/85 text-xs outline-none placeholder-white/40 resize-y"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setForm({
                                                    ...form,
                                                    faq: form.faq.filter((_, j) => j !== i),
                                                })
                                            }
                                            className="text-[10px] font-bold text-red-400 hover:text-red-300"
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setForm({
                                            ...form,
                                            faq: [...form.faq, { question: "", answer: "" }],
                                        })
                                    }
                                    className="w-full text-xs font-bold text-shariti-orange-light hover:text-shariti-orange py-2 rounded-xl border border-dashed border-white/15 hover:border-shariti-orange/40"
                                >
                                    + Ajouter une question
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "visibility" && (
                    <VisibilityEditor asso={asso} users={users} onSaved={onChanged} />
                )}

                {tab === "assignments" && (
                    <AssignmentsEditor asso={asso} onSaved={onChanged} />
                )}

                {tab === "gift_levels" && (
                    <GiftLevelsEditor form={form} setForm={setForm} />
                )}

                {tab === "quiz" && <QuizEditor associationId={asso.association_id} />}
                {tab === "stats" && <StatsView associationId={asso.association_id} />}
                {tab === "news" && <NewsEditor associationId={asso.association_id} />}
                {tab === "resources" && (
                    <AssoResourcesTab adminMode assoId={asso.association_id} />
                )}
                {tab === "partner" && (
                    <PartnerAccountTab
                        assoId={asso.association_id}
                        assoName={asso.name}
                    />
                )}

                {tab !== "visibility" && tab !== "assignments" && tab !== "quiz" && tab !== "stats" && tab !== "news" && tab !== "resources" && tab !== "partner" && (
                    <div className="mt-5 flex gap-2">
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="asso-save"
                            className="flex-1 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            <Save className="w-4 h-4" />
                            Enregistrer
                        </button>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
}

export default function AdminAssociations() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [editTab, setEditTab] = useState("info");
    const [seeding, setSeeding] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    // Bulk regen state (iter-25)
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkSections, setBulkSections] = useState(
        new Set(["history", "mission", "actions", "victories", "chiffres_cles", "argumentaire", "faq"]),
    );
    const [bulkAssoIds, setBulkAssoIds] = useState(new Set()); // empty => all
    const [bulkOnlyEmpty, setBulkOnlyEmpty] = useState(false);
    const [bulkRunning, setBulkRunning] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    const reload = async () => {
        const [a, u] = await Promise.all([
            api.get("/admin/associations"),
            api.get("/admin/users", { params: { page_size: 200 } }),
        ]);
        setItems(a.data);
        // Iter-27.15: /admin/users now returns a paginated envelope {items, total, ...}
        setUsers(u.data?.items || u.data || []);
    };
    useEffect(() => {
        reload();
    }, []);

    const seedAi = async () => {
        const ok = await confirm({
            title: "Compléter le contenu IA des associations ?",
            description:
                "L'IA va remplir les champs manquants (description courte, mission, actions, chiffres clés, argumentaire, FAQ) UNIQUEMENT pour les associations que tu as déjà créées. Aucune nouvelle association n'est créée — tu restes 100 % maître de ton catalogue. Si tu veux ajouter une nouvelle association, utilise « + Nouvelle association ». Tu peux relancer autant de fois que tu veux : il complète seulement ce qui manque.",
            confirmLabel: "Lancer la génération",
        });
        if (!ok) return;
        setSeeding(true);
        try {
            const r = await api.post("/admin/associations/seed-ai", null, { timeout: 300000 });
            const { updated = [], skipped = [], failed = [], total_assos = 0, nothing_to_do } = r.data || {};
            if (nothing_to_do) {
                toast.info(
                    "Aucune association dans le catalogue. Crée-en une avec « + Nouvelle association » avant de lancer la génération.",
                );
            } else {
                const parts = [];
                if (updated.length) parts.push(`Complétées : ${updated.length}`);
                if (skipped.length) parts.push(`Déjà OK : ${skipped.length}`);
                if (failed.length) parts.push(`Échecs : ${failed.length}`);
                toast.success(
                    parts.length
                        ? parts.join(" • ")
                        : `Tout est déjà à jour (${total_assos} associations vérifiées)`,
                );
            }
            await reload();
        } catch {
            toast.error("Le serveur a mis trop de temps. Relance — il finira ce qui manque sans dupliquer.");
            await reload();
        } finally {
            setSeeding(false);
        }
    };

    const [genQuizzes, setGenQuizzes] = useState(false);
    const generateMissingQuizzes = async () => {
        const ok = await confirm({
            title: "Générer tous les quiz manquants ?",
            description:
                "Pour chaque association sans quiz, l'IA va générer une série de 5 questions à choix unique. Si une association n'a pas encore de contenu textuel, on génère d'abord sa fiche (mission, argumentaire) PUIS le quiz. Les associations qui ont déjà un quiz sont ignorées. La génération peut prendre 1 à 3 minutes.",
            confirmLabel: "Lancer la génération",
        });
        if (!ok) return;
        setGenQuizzes(true);
        try {
            const r = await api.post(
                "/admin/associations/quizzes/generate-missing",
                null,
                { timeout: 300000 },
            );
            const { created = [], failed = [], skipped = 0, enriched = [] } = r.data || {};
            const parts = [`Quiz créés: ${created.length}`];
            if (enriched.length) parts.push(`Fiches auto-complétées: ${enriched.length}`);
            if (skipped) parts.push(`Existants: ${skipped}`);
            if (failed.length) parts.push(`Échecs: ${failed.length}`);
            toast.success(parts.join(" • "));
            await reload();
        } catch {
            toast.error("Timeout — relance pour terminer ce qui manque.");
            await reload();
        } finally {
            setGenQuizzes(false);
        }
    };

    const create = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await api.post("/admin/associations", {
                name: newName.trim(),
                short_description: "",
            });
            toast.success("Association créée");
            setNewName("");
            await reload();
        } catch {
            toast.error("Erreur");
        } finally {
            setCreating(false);
        }
    };

    const remove = async (a) => {
        const ok = await confirm({
            title: `Supprimer "${a.name}" ?`,
            description:
                "L'association et toute la progression des apprenants associée seront supprimées. Cette action est irréversible.",
            confirmLabel: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/associations/${a.association_id}`);
            toast.success("Supprimée");
            await reload();
        } catch {
            toast.error("Erreur");
        }
    };

    // Bulk regen (iter-25) — run AI regen across many associations × selected sections
    const toggleBulkSection = (id) => {
        const next = new Set(bulkSections);
        next.has(id) ? next.delete(id) : next.add(id);
        setBulkSections(next);
    };
    const toggleBulkAsso = (aid) => {
        const next = new Set(bulkAssoIds);
        next.has(aid) ? next.delete(aid) : next.add(aid);
        setBulkAssoIds(next);
    };
    const runBulkRegen = async () => {
        if (bulkSections.size === 0) {
            toast.error("Sélectionne au moins une section.");
            return;
        }
        const targetCount = bulkAssoIds.size === 0 ? items.length : bulkAssoIds.size;
        const ok = await confirm({
            title: `Régénérer ${targetCount} association${targetCount > 1 ? "s" : ""} ?`,
            description: `${bulkSections.size} section${bulkSections.size > 1 ? "s" : ""} seront ${bulkOnlyEmpty ? "complétées (champs vides uniquement)" : "écrasées"} par l'IA. La génération peut prendre 1 à 4 minutes selon le volume — ne ferme pas la fenêtre.`,
            confirmLabel: "Lancer la régénération",
        });
        if (!ok) return;
        setBulkRunning(true);
        setBulkResult(null);
        try {
            const r = await api.post(
                "/admin/associations/bulk-regenerate",
                {
                    association_ids:
                        bulkAssoIds.size === 0 ? null : Array.from(bulkAssoIds),
                    sections: Array.from(bulkSections),
                    only_empty: bulkOnlyEmpty,
                },
                { timeout: 360000 },
            );
            setBulkResult(r.data);
            const u = r.data.updated?.length || 0;
            const f = r.data.failed?.length || 0;
            const s = r.data.skipped?.length || 0;
            toast.success(
                `Régénéré · ${u} OK · ${s} ignoré${s > 1 ? "s" : ""}${f ? ` · ${f} échec${f > 1 ? "s" : ""}` : ""}`,
            );
            await reload();
        } catch (err) {
            toast.error(
                "Timeout ou erreur — relance pour reprendre là où ça s'est arrêté (rien n'est dupliqué).",
            );
        } finally {
            setBulkRunning(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-associations">
            <div className="glass-card rounded-3xl p-5 sm:p-6">
                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                    Initialisation rapide
                </p>
                <h3 className="text-xl font-extrabold text-white tracking-tight mb-2">
                    Compléter le contenu IA des associations
                </h3>
                <p className="text-sm text-white/60 font-light leading-relaxed mb-4">
                    L'IA remplit automatiquement les champs vides (description courte / mission / actions / chiffres clés / argumentaire / FAQ) pour <span className="text-white">les associations que tu as déjà créées</span>. <span className="text-white">Aucune nouvelle association n'est créée</span> — tu gardes 100 % le contrôle. Pour ajouter une asso, utilise simplement le bouton « + Nouvelle association » en haut. Tu peux relancer ce bouton autant de fois que tu veux : il complète seulement ce qui manque.
                </p>
                <button
                    onClick={seedAi}
                    disabled={seeding}
                    data-testid="seed-ai-button"
                    className="bg-shariti-orange text-white font-extrabold px-5 py-2.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center gap-2 disabled:opacity-60"
                >
                    {seeding ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Sparkles className="w-4 h-4" />
                    )}
                    {seeding ? "Génération en cours..." : "Lancer la génération IA"}
                </button>

                <div className="mt-3 pt-3 border-t border-white/5">
                    <p className="text-xs text-white/55 font-light leading-relaxed mb-3">
                        <span className="font-bold text-white">Quiz manquants ?</span> Si tu viens d'ajouter une association ou si certaines fiches affichent encore « Quiz à venir », lance la génération automatique des quiz manquants.
                    </p>
                    <button
                        onClick={generateMissingQuizzes}
                        disabled={genQuizzes}
                        data-testid="generate-missing-quizzes"
                        className="bg-shariti-yellow/20 text-shariti-yellow border border-shariti-yellow/40 font-extrabold px-4 py-2 rounded-2xl text-sm hover:bg-shariti-yellow/30 transition-all flex items-center gap-2 disabled:opacity-60"
                    >
                        {genQuizzes ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Brain className="w-3.5 h-3.5" />
                        )}
                        {genQuizzes ? "Génération en cours..." : "Générer les quiz manquants"}
                    </button>
                </div>
            </div>

            {/* Bulk regen — iter-25 */}
            <div
                className="glass-card rounded-3xl p-5 sm:p-6 border border-shariti-orange/20"
                data-testid="bulk-regen-card"
            >
                <button
                    type="button"
                    onClick={() => setBulkOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-3 text-left"
                    data-testid="bulk-regen-toggle"
                >
                    <div className="min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                            Régénération en lot
                        </p>
                        <h3 className="text-xl font-extrabold text-white tracking-tight">
                            Mettre à jour le contenu IA en masse
                        </h3>
                        <p className="text-sm text-white/55 font-light leading-relaxed mt-1">
                            Choisis les associations et les sections (Mission, Actions, Chiffres clés, Argumentaire, FAQ) à régénérer via Claude. Idéal après un changement de prompt ou pour rafraîchir d'un coup une cohorte.
                        </p>
                    </div>
                    <RefreshCw
                        className={`w-5 h-5 text-shariti-orange shrink-0 transition-transform ${
                            bulkOpen ? "rotate-180" : ""
                        }`}
                    />
                </button>

                {bulkOpen && (
                    <div className="mt-5 space-y-5" data-testid="bulk-regen-panel">
                        {/* Sections */}
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 mb-2">
                                Sections à régénérer
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { id: "history", label: "Histoire" },
                                    { id: "mission", label: "Mission" },
                                    { id: "actions", label: "Actions" },
                                    { id: "victories", label: "Réussites" },
                                    { id: "chiffres_cles", label: "Chiffres clés" },
                                    { id: "argumentaire", label: "Argumentaire" },
                                    { id: "faq", label: "FAQ" },
                                ].map((s) => {
                                    const on = bulkSections.has(s.id);
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => toggleBulkSection(s.id)}
                                            data-testid={`bulk-section-${s.id}`}
                                            className={`px-3.5 py-2 rounded-2xl text-xs font-extrabold transition-all border ${
                                                on
                                                    ? "bg-shariti-orange/20 text-shariti-orange-light border-shariti-orange/40"
                                                    : "bg-white/5 text-white/50 border-white/5 hover:bg-white/10"
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setBulkSections(
                                            new Set([
                                                "history",
                                                "mission",
                                                "actions",
                                                "victories",
                                                "chiffres_cles",
                                                "argumentaire",
                                                "faq",
                                            ]),
                                        )
                                    }
                                    data-testid="bulk-sections-all"
                                    className="px-3.5 py-2 rounded-2xl text-xs font-bold text-white/40 hover:text-white/80 transition-all"
                                >
                                    Tout
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setBulkSections(new Set())}
                                    className="px-3.5 py-2 rounded-2xl text-xs font-bold text-white/40 hover:text-white/80 transition-all"
                                >
                                    Aucune
                                </button>
                            </div>
                        </div>

                        {/* Associations */}
                        <div>
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">
                                    Associations cibles{" "}
                                    <span className="text-white/30">
                                        ({bulkAssoIds.size === 0
                                            ? `toutes — ${items.length}`
                                            : `${bulkAssoIds.size} sélectionnée${bulkAssoIds.size > 1 ? "s" : ""}`})
                                    </span>
                                </p>
                                <div className="flex gap-1">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setBulkAssoIds(
                                                new Set(items.map((a) => a.association_id)),
                                            )
                                        }
                                        data-testid="bulk-assos-all"
                                        className="text-[10px] uppercase tracking-widest font-bold text-shariti-orange-light hover:text-shariti-orange"
                                    >
                                        Tout sélectionner
                                    </button>
                                    <span className="text-white/20">|</span>
                                    <button
                                        type="button"
                                        onClick={() => setBulkAssoIds(new Set())}
                                        className="text-[10px] uppercase tracking-widest font-bold text-white/40 hover:text-white/70"
                                    >
                                        Réinitialiser
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                                {items.map((a) => {
                                    const on = bulkAssoIds.has(a.association_id);
                                    return (
                                        <label
                                            key={a.association_id}
                                            data-testid={`bulk-asso-${a.association_id}`}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-all border ${
                                                on
                                                    ? "bg-shariti-orange/15 border-shariti-orange/30"
                                                    : "bg-white/5 border-white/5 hover:bg-white/10"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={() =>
                                                    toggleBulkAsso(a.association_id)
                                                }
                                                className="w-4 h-4 rounded accent-shariti-orange shrink-0"
                                            />
                                            <span className="text-xs font-bold text-white truncate flex-1">
                                                {a.name}
                                            </span>
                                            {!a.mission && (
                                                <span
                                                    className="text-[9px] uppercase tracking-widest font-bold text-amber-400 shrink-0"
                                                    title="Contenu vide"
                                                >
                                                    vide
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                            <p className="text-[10px] text-white/35 mt-1.5 italic">
                                Aucune coche = on traite TOUTES les associations.
                            </p>
                        </div>

                        {/* Mode */}
                        <label
                            className="flex items-center gap-2.5 cursor-pointer select-none"
                            data-testid="bulk-only-empty"
                        >
                            <input
                                type="checkbox"
                                checked={bulkOnlyEmpty}
                                onChange={(e) => setBulkOnlyEmpty(e.target.checked)}
                                className="w-4 h-4 rounded accent-shariti-orange"
                            />
                            <span className="text-xs text-white/70 font-light">
                                <span className="font-bold text-white">Mode prudent : </span>
                                ne remplir que les sections vides (ne pas écraser le contenu existant).
                            </span>
                        </label>

                        {/* Run */}
                        <button
                            type="button"
                            onClick={runBulkRegen}
                            disabled={bulkRunning || bulkSections.size === 0}
                            data-testid="bulk-regen-run"
                            className="w-full sm:w-auto bg-shariti-orange text-white font-extrabold px-5 py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {bulkRunning ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Sparkles className="w-4 h-4" />
                            )}
                            {bulkRunning
                                ? "Régénération en cours… (1 à 4 min)"
                                : `Lancer la régénération (${
                                      bulkAssoIds.size === 0 ? items.length : bulkAssoIds.size
                                  } × ${bulkSections.size})`}
                        </button>

                        {bulkResult && (
                            <div
                                className="rounded-2xl bg-black/30 border border-white/5 p-4 text-xs space-y-2"
                                data-testid="bulk-regen-result"
                            >
                                <p className="text-white/80 font-bold">
                                    Résultat ({bulkResult.total} traitée
                                    {bulkResult.total > 1 ? "s" : ""})
                                </p>
                                {bulkResult.updated?.length > 0 && (
                                    <p className="text-emerald-400">
                                        ✓ Mis à jour ({bulkResult.updated.length}) :{" "}
                                        <span className="text-white/60 font-light">
                                            {bulkResult.updated.map((u) => u.name).join(", ")}
                                        </span>
                                    </p>
                                )}
                                {bulkResult.skipped?.length > 0 && (
                                    <p className="text-white/50">
                                        ↷ Ignorés ({bulkResult.skipped.length}) :{" "}
                                        <span className="font-light">
                                            {bulkResult.skipped.map((u) => u.name).join(", ")}
                                        </span>
                                    </p>
                                )}
                                {bulkResult.failed?.length > 0 && (
                                    <p className="text-red-400">
                                        ✗ Échecs ({bulkResult.failed.length}) :{" "}
                                        <span className="text-white/60 font-light">
                                            {bulkResult.failed
                                                .map((u) => u.name)
                                                .join(", ")}
                                        </span>
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="glass-card rounded-3xl p-5">
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Nom d'une nouvelle association..."
                        data-testid="new-asso-name"
                        className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none"
                    />
                    <button
                        onClick={create}
                        disabled={creating || !newName.trim()}
                        data-testid="create-asso-button"
                        className="px-4 py-3 rounded-2xl bg-shariti-orange text-white font-bold flex items-center gap-2 disabled:opacity-50"
                    >
                        <Plus className="w-4 h-4" />
                        Créer
                    </button>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="glass-card rounded-3xl p-10 text-center text-white/50">
                    Aucune association pour le moment.
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((a) => {
                        const visMode = a.visibility?.mode || "specific";
                        const visCount = (a.visibility?.user_ids || []).length;
                        return (
                            <div
                                key={a.association_id}
                                className="glass-card rounded-2xl p-4 flex items-center gap-4"
                                data-testid={`asso-row-${a.association_id}`}
                            >
                                <div className="w-12 h-12 rounded-2xl bg-shariti-orange/15 flex items-center justify-center font-extrabold text-white shrink-0">
                                    {a.logo_url ? (
                                        <img
                                            src={a.logo_url}
                                            alt={a.name}
                                            className="w-full h-full object-contain p-1"
                                        />
                                    ) : (
                                        (a.name || "?")[0]?.toUpperCase()
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-extrabold text-white truncate">
                                        {a.name}
                                    </p>
                                    <p className="text-[11px] text-white/50 truncate flex items-center gap-1.5">
                                        {visMode === "all" ? (
                                            <>
                                                <Eye className="w-3 h-3" /> Visible par tout le monde
                                            </>
                                        ) : visCount === 0 ? (
                                            <>
                                                <EyeOff className="w-3 h-3 text-amber-400" /> Visible par personne
                                            </>
                                        ) : (
                                            <>
                                                <UserCheck className="w-3 h-3 text-shariti-orange-light" />{" "}
                                                {visCount} recruteur{visCount > 1 ? "s" : ""}
                                            </>
                                        )}
                                        {" · "}
                                        {a.mission ? "✓ contenu IA" : "⚠️ contenu vide"}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setEditing(a);
                                        setEditTab("visibility");
                                    }}
                                    data-testid={`asso-visibility-${a.association_id}`}
                                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-shariti-orange/20 hidden sm:flex items-center gap-1.5 text-xs font-bold text-shariti-orange-light"
                                    title="Gérer les recruteurs"
                                >
                                    <UserCheck className="w-3.5 h-3.5" />
                                    {visMode === "all" ? "Tous" : visCount > 0 ? `${visCount} recruteur${visCount > 1 ? "s" : ""}` : "Personne"}
                                </button>
                                <button
                                    onClick={() => {
                                        setEditing(a);
                                        setEditTab("info");
                                    }}
                                    data-testid={`asso-edit-${a.association_id}`}
                                    className="p-2.5 rounded-xl bg-white/5 hover:bg-shariti-orange/20"
                                    title="Modifier"
                                >
                                    <Edit3 className="w-4 h-4 text-white/70" />
                                </button>
                                <button
                                    onClick={() => remove(a)}
                                    data-testid={`asso-delete-${a.association_id}`}
                                    className="p-2.5 rounded-xl bg-white/5 hover:bg-red-500/20"
                                    title="Supprimer"
                                >
                                    <Trash2 className="w-4 h-4 text-white/70" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <AssociationEditor
                    asso={editing}
                    users={users}
                    initialTab={editTab}
                    onClose={() => setEditing(null)}
                    onChanged={reload}
                />
            )}
        </div>
    );
}
